import { useEffect, useState } from "react";
import { Loader2, Music2, Upload, ArrowRight, KeyRound, Landmark, Zap, Sparkles } from "lucide-react";
import { mistApi, pipelinesApi } from "@/api/mist";
import { setSongOrigin, consumeSongResult } from "@/lib/directorPrefill";
import ProjectNameCombobox from "@/components/mist/ProjectNameCombobox";
import { toast } from "sonner";

const LENGTHS = [[15, "15s Shorts"], [30, "30s Reel"], [60, "60s Verse"]];
const SOURCES = [["upload", "Device Upload", Upload], ["gallery", "Gallery", Landmark], ["create", "Create Song", Zap]];
const DRAFT_KEY = "mist_ws1_draft";
const DRAFT_TTL = 24 * 60 * 60 * 1000; // 24h retention tier (client rehydrate)

const clampMMSS = (v) => {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec((v || "").trim());
  if (!m) return null;
  return Math.max(0, parseInt(m[1], 10) * 60 + Math.min(59, parseInt(m[2], 10)));
};
const fallbackName = () => `Project_Track_${new Date().toISOString().slice(0, 10)}`;

// Workspace 1 — the Audio Engine. Strict top→bottom chain: Project Name →
// Source (Device / Gallery / Create Song) → source view → cut length → start
// offset → Enter Director Studio. Draft state is auto-saved for 24h so a crash
// mid-flow (esp. the Create-Song render loop) rehydrates cleanly on return.
export default function WorkspaceAudioSetup({ onLocked }) {
  const [projectName, setProjectName] = useState("");
  const [source, setSource] = useState("upload"); // upload | gallery | create
  const [comm, setComm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [srcId, setSrcId] = useState("");
  const [srcName, setSrcName] = useState("");
  const [length, setLength] = useState(30);
  const [startStr, setStartStr] = useState("00:00");
  const [locking, setLocking] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Rehydrate a recent (<24h) draft on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && Date.now() - (d.ts || 0) < DRAFT_TTL) {
          setProjectName(d.projectName || ""); setSource(d.source || "upload");
          setPrompt(d.prompt || ""); setLength(d.length || 30);
          setStartStr(d.startStr || "00:00");
          if (d.srcId) { setSrcId(d.srcId); setSrcName(d.srcName || ""); }
        }
      }
    } catch { /* ignore */ }
    // Hand-back from the full Mist Music Studio (Create Song round-trip).
    const song = consumeSongResult();
    if (song?.assetId) {
      setSource("create"); setSrcId(song.assetId); setSrcName(song.title || "AI Track");
      toast.success("Track added from Music Studio ✓");
    }
    setHydrated(true);
  }, []);

  // Secure state to the 24h draft the moment any field changes (only AFTER the
  // rehydrated state has committed, so we never overwrite a good draft with defaults).
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        ts: Date.now(), projectName, source, prompt, length, startStr, srcId, srcName,
      }));
    } catch { /* ignore */ }
  }, [hydrated, projectName, source, prompt, length, startStr, srcId, srcName]);

  useEffect(() => {
    if (source === "gallery" && comm === null)
      mistApi.communityVault().then((d) => setComm(d.audio || [])).catch(() => setComm([]));
  }, [source, comm]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !requireName()) return;
    setUploading(true); const t = toast.loading("Uploading your track…");
    try { const up = await pipelinesApi.uploadSeedAudio(f); setSrcId(up.asset_id); setSrcName(f.name); toast.success("Track added ✓", { id: t }); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Upload failed", { id: t }); }
    finally { setUploading(false); }
  };

  const pickComm = async (it) => {
    if (!requireName()) return;
    const t = toast.loading("Adding to your library…");
    try { const r = await mistApi.communityUse(it.id); setSrcId(r.asset_id || r.id); setSrcName(it.title || it.filename || "Shared track"); toast.success("Track added ✓", { id: t }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add song", { id: t }); }
  };

  // Project Name is the required anchor for every source (Device / Gallery /
  // Create Song) — it labels the "Use in <Project>" hand-back from Music Studio.
  const requireName = () => {
    if (projectName.trim()) return true;
    toast.error("Enter project name first");
    return false;
  };

  // Create Song → hand off to the full Mist Music Studio (lyrics · produce ·
  // trim · save). Stash where we came from so it can bounce the track back here.
  const openMusicStudio = () => {
    if (!requireName()) return;
    const name = projectName.trim();
    setSongOrigin({ returnUrl: window.location.search.replace(/^\?/, ""), label: name, projectName: name });
    window.history.pushState({}, "", `${window.location.pathname}?studio=music&view=studio`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const enter = async () => {
    if (!srcId || locking) return;
    const start = clampMMSS(startStr);
    if (start == null) { toast.error("Start time must be MM:SS"); return; }
    const name = projectName.trim() || fallbackName();
    const manifest = {
      project_context: {
        custom_name: name,
        selected_source_mode: source === "upload" ? "DEVICE_UPLOAD" : source === "gallery" ? "GALLERY" : "CREATE_AI_SONG",
        payload_parameters: { prompt_string: prompt.trim(), target_duration_mode: `${length}s_reel`, starting_offset_seconds: start },
      },
    };
    setLocking(true); const t = toast.loading("Slicing & transcribing your cut…");
    try {
      const r = await mistApi.singerLockTrack({ source_asset_id: srcId, start, length });
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      toast.success("Locked ✓ entering Director Studio", { id: t });
      onLocked({ assetId: r.asset_id, title: r.title || name, length: r.duration || length, projectName: name, manifest });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't lock the cut", { id: t }); setLocking(false); }
  };

  return (
    <div className="mx-auto max-w-lg space-y-5 p-4" data-testid="ws-audio-setup">
      <div>
        <p className="font-mono text-[10px] font-black uppercase tracking-[0.24em] text-[#FFE08A]">Workspace 1 · Audio Engine</p>
        <h2 className="font-serif text-xl text-white">Set up your track</h2>
      </div>

      {/* Step 1 · Project Name (identity anchor) */}
      <div className="space-y-1.5">
        <label className="font-mono text-[11px] font-black uppercase tracking-widest text-white/90">Project Name</label>
        <ProjectNameCombobox value={projectName} onChange={setProjectName} testid="ws-audio-project-name"
          placeholder={fallbackName()} maxLength={80}
          className="w-full border-2 border-[#FFE08A]/50 bg-[#111] px-3 py-2.5 font-mono text-[14px] text-white outline-none focus:border-[#FFE08A]" />
      </div>

      {/* Step 2 · Source switch */}
      <div className="grid grid-cols-3 gap-1.5" data-testid="ws-audio-source">
        {SOURCES.map(([v, l, Ic]) => (
          <button key={v} data-testid={`ws-audio-src-${v}`} onClick={() => setSource(v)}
            className={`flex items-center justify-center gap-1.5 border-2 border-black py-2 font-mono text-[9px] font-black uppercase tracking-widest ${source === v ? "bg-[#FFE08A] text-black" : "bg-[#121212] text-white/80"}`}><Ic size={12} /> {l}</button>
        ))}
      </div>

      {/* Step 3 · Dynamic source view */}
      {source === "upload" && (
        <label data-testid="ws-audio-dropzone" onClick={(e) => { if (!requireName()) e.preventDefault(); }} className="flex cursor-pointer items-center justify-center gap-2 border-2 border-dashed border-[#FFE08A]/40 bg-[#0f0f0f] py-5 font-mono text-[10px] font-black uppercase tracking-widest text-[#FFE08A]/80 hover:border-[#FFE08A]">
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Choose an audio file</>}
          <input type="file" accept="audio/*" className="hidden" data-testid="ws-audio-file" disabled={uploading} onChange={onUpload} />
        </label>
      )}
      {source === "gallery" && (
        <div className="max-h-44 space-y-1 overflow-y-auto pr-1" data-testid="ws-audio-comm-list">
          {comm === null ? <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-white/50" /></div>
            : comm.length === 0 ? <p className="py-3 text-center font-mono text-[12px] text-white/70">No shared songs yet.</p>
              : comm.map((it) => (
                <button key={it.id} data-testid={`ws-audio-comm-${it.id}`} onClick={() => pickComm(it)} className="flex w-full items-center gap-2 border-2 border-[#1f1f1f] bg-[#111] px-2.5 py-2 text-left hover:border-[#FFE08A]/50">
                  <Landmark size={12} className="shrink-0 text-[#FFE08A]" /><span className="flex-1 truncate font-mono text-[12px]">{it.title || it.filename || "Shared track"}</span>{it.by && <span className="shrink-0 font-mono text-[9px] text-white/40">@{it.by}</span>}
                </button>
              ))}
        </div>
      )}
      {source === "create" && (
        <div className="space-y-2" data-testid="ws-audio-create">
          <p className="font-mono text-[11px] leading-relaxed text-white/65">
            Compose a fresh track in the full <span className="text-[#B69CFF]">Mist Music Studio</span> — write lyrics, produce, trim &amp; save. When it's ready, tap <span className="text-[#FFE08A]">Use in {projectName.trim() || "your project"}</span> to drop it straight back here.
          </p>
          <button onClick={openMusicStudio} data-testid="ws-audio-create-open"
            className="flex w-full items-center justify-center gap-2 border-2 border-black bg-[#B69CFF] py-2.5 font-mono text-[10px] font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
            <Sparkles size={13} /> Open Mist Music Studio <ArrowRight size={13} />
          </button>
        </div>
      )}

      {/* selected track chip */}
      {srcId && (
        <div className="flex items-center gap-2 border-2 border-[#43d17a]/50 bg-[#43d17a]/10 px-3 py-2" data-testid="ws-audio-selected">
          <Music2 size={13} className="shrink-0 text-[#43d17a]" /><p className="truncate font-mono text-[11px] text-white/90">{srcName || "Track ready"}</p>
        </div>
      )}

      {/* Step 4 · Format slicers */}
      <div className="space-y-1.5">
        <p className="font-mono text-[10px] font-black uppercase tracking-widest text-white/70">Cut length</p>
        <div className="grid grid-cols-3 gap-1.5" data-testid="ws-audio-lengths">
          {LENGTHS.map(([s, l]) => (
            <button key={s} data-testid={`ws-audio-len-${s}`} onClick={() => setLength(s)}
              className={`border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest ${length === s ? "bg-[#FFE08A] text-black" : "bg-[#121212] text-white/80"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Step 5 · Start offset (above Enter) */}
      <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/70">Start at
        <input value={startStr} onChange={(e) => setStartStr(e.target.value)} data-testid="ws-audio-start" placeholder="00:00"
          className="w-20 border-2 border-white/15 bg-black px-2 py-1.5 text-center font-mono text-[12px] text-white outline-none focus:border-[#FFE08A]/60" />
        <span className="text-white/40">MM:SS</span>
      </label>

      <button onClick={enter} disabled={!srcId || locking} data-testid="ws-audio-enter"
        className={`flex w-full items-center justify-center gap-2 border-2 border-black py-3.5 font-mono text-[12px] font-black uppercase tracking-widest ${srcId && !locking ? "bg-[#FFE08A] text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none" : "cursor-not-allowed bg-[#1a1a1a] text-white/60"}`}>
        {locking ? <><Loader2 size={15} className="animate-spin" /> Locking…</> : <><KeyRound size={15} /> Enter Director Studio <ArrowRight size={14} /></>}
      </button>
    </div>
  );
}
